/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { Button as BaseButton } from 'baseui/button';
import { Heading, HeadingLevel } from 'baseui/heading';
import { StyledBodyCell, StyledHeadCell, StyledTable } from 'baseui/table-grid';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import { UserIcon, UserPill } from '@gtintel/platform-atoms';
import { ROUTES } from '@gtintel/platform-constants';
import { COLOURS, convertCurrencySymbolToCode, formatPrice, getMargin, hexToRGB, parsePrice } from '@gtintel/platform-utils';
import DueDatePill from '../../components/DueDatePill';
import { MarginProgressBar } from '../../components/MarginProgress';
import { RequestStatusPill } from '../../components/RequestStatusIcon';
import { StatusDisplay } from '../../components/StatusDisplay';
import { DataPills } from './DataPills';
export const ProjectAssignmentsTable = ({
  project
}) => {
  const [projectMargin, setProjectMargin] = useState(1);
  const [css] = useStyletron();
  useEffect(() => {
    setProjectMargin(getMargin(parsePrice(project.assignment_costs ?? '0') + parsePrice(project.qa_cost) + project.partner_adjustments_cost, parsePrice(project.final_price) + project.client_adjustments_cost));
  }, [project.assignment_costs, project.final_price]);
  return <article key={nanoid()} className={css({
    backgroundColor: '#fff',
    border: `2px solid ${COLOURS.borderGray}`,
    borderRadius: '14px',
    height: 'fit-content',
    marginBottom: '24px',
    overflow: 'hidden'
  })}>
      <HeadingLevel>
        <div className={css({
        alignItems: 'center',
        display: 'flex',
        flexFlow: 'row nowrap',
        justifyContent: 'space-between',
        margin: '8px 14px'
      })}>
          <Heading styleLevel={3}>
            <a className={css({
            color: 'black',
            textDecoration: 'underline'
          })} href={`${ROUTES.ADMIN_PROJECT_DETAIL_NEW}/${project.id}/overview`}>
              {project.name}
            </a>
          </Heading>
          <StatusDisplay status={project.status} />
        </div>
        <div className={css({
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '42px',
        margin: '0 14px',
        '@media (max-width: 768px)': {
          gridTemplateColumns: '1fr'
        }
      })}>
          <div>
            <p className={css({
            margin: '0'
          })}>
              <strong>ID:</strong> {project.id}
            </p>
            <p className={css({
            margin: '0'
          })}>
              <strong>Client:</strong> {project.client}
            </p>
            <p className={css({
            margin: '0'
          })}>
              <strong>Template:</strong> {project.template}
            </p>
            <p className={css({
            margin: '0'
          })}>
              <strong>Created Date:</strong>{' '}
              {project.created_date ? DateTime.fromISO(project.created_date).toISODate() : 'Not set'}
            </p>
            <p className={css({
            margin: '0'
          })}>
              <strong>Start Date:</strong>{' '}
              {project.start_date ? DateTime.fromISO(project.start_date).toISODate() : 'Not set'}
            </p>
            <p className={css({
            margin: '0'
          })}>
              <strong>Due Date:</strong>{' '}
              {project.requested_completion_date ? DateTime.fromISO(project.requested_completion_date).toISODate() : 'Not set'}
            </p>
            <p className={css({
            margin: '0'
          })}>
              <strong>Last Updated:</strong>{' '}
              {project.updated_date ? DateTime.fromISO(project.updated_date).toISODate() : 'Not set'}
            </p>
          </div>
          <div>
            <p className={css({
            margin: '0'
          })}>
              <strong>Client Price: </strong>
              {formatPrice(+project.final_price, convertCurrencySymbolToCode(project.currency))}{' '}
              <span className={css({
              color: COLOURS.gray,
              fontStyle: 'italic'
            })}>
                (Before Discount)
              </span>
              <div className={css({
              width: '50%'
            })}>
                <MarginProgressBar projectMargin={projectMargin} />
              </div>
            </p>

            <div className={css({
            display: 'flex',
            flexFlow: 'row wrap',
            '@media (max-width: 768px)': {
              flexFlow: 'column'
            }
          })}>
              <p className={css({
              margin: '0 24px 0 0'
            })}>
                <strong>Ops Lead: </strong>{' '}
                <UserPill user={project.cx_lead} showName hasBorder empty_message="No Ops Lead Assigned" />
              </p>
              <p className={css({
              margin: '0'
            })}>
                <strong>Partner: </strong>{' '}
                <UserPill user={project.partner} showName hasBorder empty_message="No Partner Assigned" />
              </p>
            </div>
          </div>
        </div>
        &nbsp;
      </HeadingLevel>
      <RequestTable key={nanoid()} project={project} />
    </article>;
};
const RequestTable = ({
  project
}) => {
  const [css] = useStyletron();
  const isLeadOrQa = a => a.is_lead_partner_assignment || a.is_qa_assignment;
  const leadOrQaColour = a => {
    if (!isLeadOrQa(a)) {
      return COLOURS.black;
    }
    return a.is_lead_partner_assignment ? COLOURS.gold : COLOURS.blue;
  };
  const background = (a, index) => {
    const colour = leadOrQaColour(a);
    return a.is_lead_partner_assignment || a.is_qa_assignment ? hexToRGB(colour, 0.1) : index % 2 !== 0 ? '#f6f6f6' : 'transparent';
  };
  const leadOrQA = a => {
    if (!isLeadOrQa(a)) {
      return '';
    }
    return a.is_lead_partner_assignment ? 'Lead Assignment' : 'QA Assignment';
  };
  const generateStyles = (row, index, backgroundFunc) => {
    return {
      alignItems: 'center',
      background: backgroundFunc(row, index),
      display: 'flex'
    };
  };
  return <div data-testid="assignment-table" className={css({
    maxWidth: 'calc(100vw - 98px)',
    width: '100%',
    '@media (max-width: 768px)': {
      maxWidth: 'calc(100vw - 120px)'
    }
  })}>
      <StyledTable role="grid" $gridTemplateColumns="minmax(100px, max-content) minmax(200px, max-content) 100px 200px 2fr 1.5fr 150px minmax(150px, .5fr) 100px" className={css({
      borderWidth: '0',
      borderTopWidth: '1px',
      borderLeftWidth: '0 !important',
      borderRightWidth: '0 !important',
      borderBottomWidth: '0 !important',
      borderRadius: '0 !important',
      marginBottom: '-4px'
    })}>
        <div role="row" className={css({
        display: 'contents'
      })}>
          <StyledHeadCell>Status</StyledHeadCell>
          <StyledHeadCell>Partner</StyledHeadCell>
          <StyledHeadCell>Fee</StyledHeadCell>
          <StyledHeadCell>Due Date</StyledHeadCell>
          <StyledHeadCell>Subjects</StyledHeadCell>
          <StyledHeadCell>Jurisdictions</StyledHeadCell>
          <StyledHeadCell>Last Updated</StyledHeadCell>
          <StyledHeadCell></StyledHeadCell>
          <StyledHeadCell></StyledHeadCell>
        </div>
        {project.assignments.map((row, i) => {
        return <div key={nanoid()} role="row" className={css({
          display: 'contents'
        })}>
              <StyledBodyCell $striped={i % 2 !== 0} style={generateStyles(row, i, background)}>
                <RequestStatusPill status={row.status} showIcon={false} />
              </StyledBodyCell>
              <StyledBodyCell $striped={i % 2 !== 0} style={generateStyles(row, i, background)}>
                <UserIcon user={row.partner} showName={true} />
              </StyledBodyCell>
              <StyledBodyCell $striped={i % 2 !== 0} style={generateStyles(row, i, background)}>
                <strong>{project.currency}</strong> {row.fee}
              </StyledBodyCell>
              <StyledBodyCell $striped={i % 2 !== 0} style={generateStyles(row, i, background)}>
                <DueDatePill dueDate={row.due_date} submittedDate={row.submitted_date} />
              </StyledBodyCell>
              <StyledBodyCell $striped={i % 2 !== 0} style={generateStyles(row, i, background)}>
                {<DataPills data={row.components.filter(component => component.subject).map(component => {
              return component.subject?.name;
            })} />}
              </StyledBodyCell>
              <StyledBodyCell $striped={i % 2 !== 0} style={generateStyles(row, i, background)}>
                <DataPills data={row.components.filter(component => component.jurisdiction).map(component => {
              return component.jurisdiction.name;
            })} />
              </StyledBodyCell>
              <StyledBodyCell $striped={i % 2 !== 0} style={generateStyles(row, i, background)}>
                <strong className={css({
              color: COLOURS.black
            })} title={row.updated_at ? row.updated_at.split('T').join(' ').split('.')[0] : ''}>
                  {row.updated_at ? DateTime.fromISO(row.updated_at).toISODate() : ''}
                </strong>
              </StyledBodyCell>
              <StyledBodyCell $striped={i % 2 !== 0} style={generateStyles(row, i, (row, i) => {
            return isLeadOrQa(row) && leadOrQaColour(row);
          })}>
                <strong className={css({
              color: COLOURS.white
            })}>
                  {leadOrQA(row)}
                </strong>
              </StyledBodyCell>
              <StyledBodyCell $striped={i % 2 !== 0} style={generateStyles(row, i, background)}>
                <BaseButton $as={'a'} className={css({
              alignItems: 'center',
              display: 'flex',
              flexFlow: 'row nowrap',
              gap: '8px',
              padding: '4px 8px'
            })} href={`${ROUTES.ADMIN_PROJECT_DETAIL_NEW}/${project.id}/assignments/${row.id}`} target="_blank" rel="noreferrer" kind="tertiary" size="compact">
                  <p className={css({
                margin: '0'
              })}>View</p>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
                </BaseButton>
              </StyledBodyCell>
            </div>;
      })}
      </StyledTable>
    </div>;
};
export default RequestTable;